import {
	TCompany,
	TCompanyBilling,
	TCompanyBillingUpdate,
	TCompanyCreate,
	TCompanyUpdate,
	TCompanyCollection,
} from "@/types/TCompany";
import { TFileUploadResponse } from "@/types/TFiles";
import { TDatesFilter } from "@/types/TFilters";
import { TProjectSummary } from "@/types/TProject";

import API from "./api";

class CompaniesServiceClass {
	fetchAll = (
		collection: TCompanyCollection = "companies"
	): Promise<TCompany[]> => {
		return API.get(collection).then((res) => res.data);
	};

	fetchCompany = (id: number): Promise<TCompany> => {
		return API.get(`companies/${id}`).then((res) => res.data);
	};

	/** `companies` stores the payload's `type` (OWNER by default); `clients` always stores CLIENT. */
	create = (
		company: TCompanyCreate,
		collection: TCompanyCollection = "companies"
	): Promise<TCompany> => {
		return API.post(collection, company).then((res) => res.data);
	};

	/** Partial: a field left out is untouched, `""` clears it. */
	update = (company: Partial<TCompanyUpdate>) => {
		return API.patch(`companies/${company.id}`, company).then(
			(res) => res.data
		);
	};

	/** Soft delete server-side — the company disappears from the lists but stays on existing reports. */
	delete = (id: number): Promise<void> => {
		return API.delete(`companies/${id}`).then(() => {});
	};

	/** A client's projects with their figures; `period` empty = the current month, server side. */
	fetchProjectsSummary = (
		id: number,
		period: TDatesFilter = {}
	): Promise<TProjectSummary[]> => {
		return API.get(`companies/${id}/projects/summary`, {
			params: period,
		}).then((res) => res.data);
	};

	/** The defaults when never saved — never a 404. */
	fetchBilling = (id: number): Promise<TCompanyBilling> => {
		return API.get(`companies/${id}/billing`).then((res) => res.data);
	};

	/** PUT: the whole form; a field left out goes back to its default. */
	updateBilling = (
		id: number,
		billing: TCompanyBillingUpdate
	): Promise<TCompanyBilling> => {
		return API.put(`companies/${id}/billing`, billing).then(
			(res) => res.data
		);
	};

	updateLogo = (
		file: File,
		companyId?: number
	): Promise<TFileUploadResponse> => {
		const formData = new FormData();
		formData.append("logo", file);

		return API.post(`companies/${companyId}/logo`, formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		}).then((res) => res.data);
	};
}

const CompaniesService = new CompaniesServiceClass();
export default CompaniesService;
